import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    query,
    orderBy
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Contractor } from '../types';

const CONTRACTORS_COLLECTION = 'contractors';

// Get all contractors
export async function getContractors(): Promise<Contractor[]> {
    const q = query(collection(db, CONTRACTORS_COLLECTION), orderBy('name'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Contractor));
}

// Get a single contractor by ID
export async function getContractor(id: string): Promise<Contractor | null> {
    const docRef = doc(db, CONTRACTORS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as Contractor;
}

// Create or update a contractor
export async function saveContractor(contractor: Contractor): Promise<void> {
    const docRef = doc(db, CONTRACTORS_COLLECTION, contractor.id);
    await setDoc(docRef, contractor, { merge: true });
}

// Update contractor's last sync time
export async function updateContractorSyncTime(id: string): Promise<void> {
    const docRef = doc(db, CONTRACTORS_COLLECTION, id);
    await updateDoc(docRef, { lastSyncAt: new Date().toISOString() });
}

// Seed data for initial contractors
export const SEED_CONTRACTORS: Contractor[] = [
    {
        id: 'ternion-llc',
        name: 'Ternion LLC',
        displayName: 'Ternion',
        contacts: [
            { name: 'Mario A. R. Chavarria', role: 'Chairman & CEO' },
            { name: 'Juan D. Davila', role: 'Chief Commercial Officer', email: 'J.Davila@Ternion.US' }
        ],
        naicsCodes: ['237120', '237130', '238210', '238220', '238290', '541330', '541512', '541519', '561621'],
        keywords: ['instrumentation', 'commissioning', 'control systems', 'SCADA', 'pipeline', 'substation', 'industrial construction'],
        excludeKeywords: ['residential', 'software-only', 'commercial building'],
        preferredAgencies: ['DOE', 'DOD', 'Army Corps of Engineers', 'VA'],
        preferredSetAsides: ['SBA', 'SDVOSB'],
        markets: ['Oil & Gas', 'Power & Energy', 'Chemicals & Refining', 'Carbon Capture & Hydrogen', 'Aerospace'],
        notes: 'NOT interested in general commercial federal contracting. Focus on technical/industrial scopes: utility infrastructure, R&D, fuel systems, power generation.',
        createdAt: new Date().toISOString(),
        capabilityStatement: "Ternion LLC represents a conglomerate of companies with over 40 years of experience in the Federal Market. We specialize in general construction, heavy civil work, mechanical/electrical systems, and industrial automation. Our team has successfully executed projects for the DOD, DOE, and Army Corps of Engineers, delivering high-complexity infrastructure solutions.",
        personnel: {
            count: 25,
            clearances: ['Public Trust', 'Secret', 'DOE Q'],
            certifications: ['PMP', 'LEED AP', 'CMMI Level 2']
        },
        preferences: {
            minContractValue: 250000,
            maxContractValue: 50000000,
            preferredLocations: ['TX', 'NM', 'LA', 'OK']
        }
    },
    {
        id: 'myhorizon-ai',
        name: 'MyHorizon AI',
        displayName: 'MyHorizon',
        contacts: [
            { name: 'Daniel', role: 'Founder', email: 'daniel@myhorizon.ai' }
        ],
        naicsCodes: ['541511', '541512', '541519', '518210', '541690', '541720'],
        keywords: ['artificial intelligence', 'AI', 'machine learning', 'data labeling', 'data annotation', 'chatbot', 'data center', 'survey', 'NLP', 'natural language processing', 'automation'],
        excludeKeywords: ['construction', 'janitorial', 'landscaping'],
        preferredAgencies: ['DOD', 'NSA', 'DHS', 'NASA', 'DOE', 'NIH'],
        preferredSetAsides: ['SBA'],
        markets: ['AI/ML Services', 'Data Services', 'IT Consulting', 'Research & Development'],
        notes: 'Focus on AI-related contracts: data labeling, AI surveys, chatbots, data centers, ML model development. Can leverage existing AI tools and platforms.',
        createdAt: new Date().toISOString(),
        capabilityStatement: "MyHorizon AI provides cutting-edge artificial intelligence solutions for government agencies. We specialize in Large Language Model (LLM) deployment, data annotation/labeling services, and AI-driven workflow automation. Our platform accelerates contract discovery and analysis, enabling agencies to leverage AI for mission-critical tasks.",
        personnel: {
            count: 12,
            clearances: [],
            certifications: ['AWS Certified', 'Google Cloud Professional', 'CISSP']
        },
        preferences: {
            minContractValue: 0,
            preferredLocations: ['Remote', 'DC', 'MD', 'VA']
        }
    }
];

// Initialize contractors in Firestore if they don't exist
export async function seedContractors(): Promise<void> {
    for (const contractor of SEED_CONTRACTORS) {
        const existing = await getContractor(contractor.id);
        if (!existing) {
            await saveContractor(contractor);
            console.log(`Seeded contractor: ${contractor.name}`);
        }
    }
}
